import { json } from '@codemirror/lang-json';
import { AlertCircleIcon, PlusIcon } from '@langchain/untitled-ui-icons';

import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';

import { RunDetailsFunctionsAndStructuredOutput } from '@/Pages/Run/components/RunDetailsFunctions';
import {
  RunFancyInput,
  RunFancyOutput,
} from '@/Pages/Run/components/RunDetailsIO';
import {
  RunDetailsLLMInput,
  RunDetailsLLMOutput,
} from '@/Pages/Run/components/RunDetailsLLM';
import { RunDisplaySchema } from '@/Pages/Run/utils/types';
import { showChatMessageComponents } from '@/Pages/Run/utils/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { Button, Input, Text, Tooltip } from '@/design-system';

import { ExampleSchema, RunSchema } from '../../types/schema';
import { Code, CodeCard } from '../Code/Code';
import { FancyRenderingProvider } from '../RichTextEditor/utils/fancyRenderContext';

export function ExampleEditor(props: {
  elementRef: React.RefObject<HTMLDivElement>;
  setShowRaw: Dispatch<SetStateAction<boolean>>;
  showRaw: boolean;
  schema?: Record<string, unknown>;
  showSchema: boolean;
  setShowSchema: Dispatch<SetStateAction<boolean>>;
  example?: ExampleSchema;
  run: RunDisplaySchema;
  title: string;
  isInput: boolean;
  setInputs: Dispatch<SetStateAction<RunSchema['inputs']>>;
  setOutputs: Dispatch<SetStateAction<RunSchema['outputs']>>;
  codeEditorRef?: React.RefObject<HTMLDivElement>;
  schemaValidationError?: string;
}) {
  return (
    <div className="flex flex-col gap-2" ref={props.elementRef}>
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Text variant="h2">{props.title}</Text>
          {props.schemaValidationError && !props.showRaw && (
            <Tooltip
              title={
                <div>
                  <div className="flex max-w-[400px]">
                    {props.schemaValidationError} View raw and edit to fix the
                    error.
                  </div>
                </div>
              }
            >
              <div>
                <AlertCircleIcon className="h-5 w-5 text-error" />
              </div>
            </Tooltip>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            color="secondary"
            size="md"
            onClick={() => props.setShowRaw((prev) => !prev)}
          >
            {props.showRaw ? 'View Rendered' : 'View Raw'}
          </Button>
          {props.schema && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => props.setShowSchema((prev) => !prev)}
            >
              {!props.showSchema ? 'View JSON Schema' : 'Hide JSON Schema'}
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex-1" ref={props.codeEditorRef}>
          <ExampleRenderedIO
            run={props.run}
            setInputs={props.setInputs}
            setOutputs={props.setOutputs}
            schema={props.schema}
            elementRef={props.elementRef}
            showRaw={props.showRaw}
            setShowRaw={props.setShowRaw}
            isInput={props.isInput}
          />
        </div>
        {props.showSchema && props.schema && (
          <div className="flex flex-1 flex-col items-stretch">
            <CodeCard>
              <Code
                language={json()}
                readOnly={true}
                value={JSON.stringify(props.schema, null, 2)}
              />
            </CodeCard>
          </div>
        )}
      </div>
    </div>
  );
}

type FieldValueType = 'string' | 'number' | 'boolean' | 'json';

function AddFieldForm(props: {
  onAdd: (key: string, value: any) => void;
  onCancel: () => void;
}) {
  const [fieldKey, setFieldKey] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const [fieldType, setFieldType] = useState<FieldValueType>('string');

  const handleAddField = () => {
    let parsedValue: any = fieldValue;

    try {
      if (fieldType === 'number') {
        parsedValue = Number(fieldValue);
      } else if (fieldType === 'boolean') {
        parsedValue = fieldValue.toLowerCase() === 'true';
      } else if (fieldType === 'json') {
        parsedValue = JSON.parse(fieldValue);
      }

      props.onAdd(fieldKey, parsedValue);

      // Reset form
      setFieldKey('');
      setFieldValue('');
      setFieldType('string');
    } catch (error) {
      console.error('Failed to parse value:', error);
    }
  };

  const isValidValue = () => {
    if (fieldKey === '') return false;

    if (fieldType === 'boolean') {
      return fieldValue === 'true' || fieldValue === 'false';
    }

    if (fieldType === 'number') {
      return !isNaN(Number(fieldValue)) && fieldValue !== '';
    }

    if (fieldType === 'json') {
      try {
        JSON.parse(fieldValue);
        return true;
      } catch {
        return false;
      }
    }

    return fieldValue !== '';
  };

  return (
    <form
      className="flex items-center gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        handleAddField();
      }}
    >
      <Input
        type="text"
        placeholder="Field name"
        onChange={(value) => setFieldKey(value)}
        value={fieldKey}
        width="200px"
      />
      {fieldType === 'boolean' ? (
        <Select
          value={fieldValue}
          onValueChange={(newValue) => setFieldValue(newValue)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="true/false" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <Input
          type="text"
          placeholder={
            fieldType === 'json'
              ? 'JSON value (e.g., {"key": "value"})'
              : 'Value'
          }
          onChange={(value) => setFieldValue(value)}
          value={fieldValue}
          width="360px"
        />
      )}
      <Select
        value={fieldType}
        onValueChange={(newValue) => {
          setFieldType(newValue as FieldValueType);
        }}
      >
        <SelectTrigger className="w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="string">String</SelectItem>
          <SelectItem value="number">Number</SelectItem>
          <SelectItem value="boolean">Boolean</SelectItem>
          <SelectItem value="json">JSON</SelectItem>
        </SelectContent>
      </Select>
      <Button
        size="md"
        color="primary"
        disabled={!isValidValue()}
        type="submit"
      >
        Add
      </Button>
      <Button
        size="md"
        color="secondary"
        variant="outlined"
        type="button"
        onClick={props.onCancel}
      >
        Cancel
      </Button>
    </form>
  );
}

const ExampleRenderedIO = (props: {
  elementRef: React.RefObject<HTMLDivElement>;
  setShowRaw: Dispatch<SetStateAction<boolean>>;
  showRaw: boolean;
  schema?: Record<string, unknown>;
  run: RunDisplaySchema;
  isInput: boolean;
  setInputs: Dispatch<SetStateAction<RunSchema['inputs']>>;
  setOutputs: Dispatch<SetStateAction<RunSchema['outputs']>>;
}) => {
  let editorComponent: ReactElement | null = null;
  const [outputIndex, setOutputIndex] = useState(0);
  const [isAddingField, setIsAddingField] = useState(false);

  const showChatComponents = showChatMessageComponents(props.run.inputs);

  if (props.run?.run_type === 'llm' && showChatComponents) {
    editorComponent = props.isInput ? (
      <>
        {!props.showRaw && (
          <RunDetailsFunctionsAndStructuredOutput
            run={props.run}
            outputs={props.run.outputs}
            variant="plain"
            onEditFunctions={(functions) => {
              const tools = functions.map((fn) => {
                return {
                  type: 'function',
                  function: fn,
                };
              });
              props.setInputs({
                ...props.run.inputs,
                tools,
              });
            }}
          />
        )}
        <RunDetailsLLMInput
          run={props.run}
          inputs={props.run.inputs}
          onEditInputs={props.setInputs}
          forceRawRender={props.showRaw ? 'raw' : 'rendered'}
          schema={props.isInput ? props.schema : undefined}
          hideHeaders
        />
      </>
    ) : (
      <FancyRenderingProvider>
        <RunDetailsLLMOutput
          run={props.run}
          outputs={props.run.outputs}
          outputIndex={outputIndex}
          setOutputIndex={setOutputIndex}
          forceRawRender={props.showRaw ? 'raw' : 'rendered'}
          onEditOutputs={props.setOutputs}
          rawOutput={props.showRaw}
          setRawOutput={props.setShowRaw}
          hideHeaders
          schema={props.isInput ? undefined : props.schema}
        />
      </FancyRenderingProvider>
    );
  } else {
    editorComponent = props.isInput ? (
      <RunFancyInput
        inputs={props.run.inputs}
        run={props.run}
        onEditInputs={props.setInputs}
        forceRawRender={props.showRaw ? 'raw' : 'rendered'}
        schema={props.isInput ? props.schema : undefined}
        hideHeaders
        setRawInput={props.setShowRaw}
        rawInput={props.showRaw}
      />
    ) : (
      <RunFancyOutput
        outputs={props.run.outputs}
        run={props.run}
        onEditOutputs={props.setOutputs}
        forceRawRender={props.showRaw ? 'raw' : 'rendered'}
        schema={props.isInput ? undefined : props.schema}
        hideHeaders
        setRawOutput={props.setShowRaw}
        rawOutput={props.showRaw}
        className="p-0"
      />
    );
  }

  return (
    <div
      className="border-b border-tertiary p-0 last-of-type:border-b-0"
      ref={props.elementRef}
      data-testid={
        props.isInput ? 'example-crud-inputs' : 'example-crud-outputs'
      }
    >
      {editorComponent}

      {!props.showRaw && (
        <div className="mt-2 border-t border-tertiary pt-2">
          {isAddingField ? (
            <AddFieldForm
              onAdd={(key, value) => {
                const currentData = props.isInput
                  ? props.run.inputs
                  : props.run.outputs;
                const updatedData = {
                  ...currentData,
                  [key]: value,
                };

                if (props.isInput) {
                  props.setInputs(updatedData);
                } else {
                  props.setOutputs(updatedData);
                }

                setIsAddingField(false);
              }}
              onCancel={() => setIsAddingField(false)}
            />
          ) : (
            <Button
              variant="outlined"
              color="secondary"
              leftDecorator={PlusIcon}
              onClick={() => setIsAddingField(true)}
            >
              Add field
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
